import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../lib/api';

/**
 * Redirects the removed legacy legislator detail route
 * (/admin/legislators/:legislatorId) to its People replacement.
 *
 * Legislator rows are session-scoped roster records, so the owning person has
 * to be resolved before redirecting — the People tool is person-scoped.
 */
const LegacyLegislatorDetailRedirect: React.FC = () => {
  const { legislatorId } = useParams<{ legislatorId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const numericId = legislatorId != null ? parseInt(legislatorId, 10) : NaN;
      if (!Number.isFinite(numericId)) {
        toast.error('Invalid legislator id');
        navigate('/admin-next/people', { replace: true });
        return;
      }

      try {
        const response = await api.get(`/admin/legislators/${numericId}/full-profile`);
        if (cancelled) return;
        const personId = String(response.data?.data?.person_id ?? '').trim();
        if (personId) {
          navigate(`/admin-next/people/${personId}`, { replace: true });
          return;
        }
        toast.error('Legislator is not linked to the person registry');
        navigate('/admin-next/people', { replace: true });
      } catch {
        if (cancelled) return;
        toast.error('Could not resolve legislator profile');
        navigate('/admin-next/people', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [legislatorId, navigate]);

  return (
    <div className="flex items-center justify-center min-h-64">
      <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
    </div>
  );
};

export default LegacyLegislatorDetailRedirect;
